import {get as _get} from 'lodash';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import moment from 'moment';

import {getCompetitorsNameFromEvent} from '@utils/competitorName';
import {addHasMatchIdToStructure} from '@utils/eventObject';
import {convertToInt, getProductShortNameWithOnlyLettersAndDigits} from '@utils/helpers';
import {
    COMPETITOR_NOT_DEFINED,
    COMPETITOR_NOT_DEFINED_BOTH,
    PROPERTY_LICENCE_TYPE_OTHER,
    PROPERTY_LICENCE_TYPE_SPORTRADAR,
} from '@constants/variables';
import Table from '@appComponents/Table';
import {
    renderCategoryColumn, renderContentCategoryColumn,
    renderIdColumn, renderProductsColumn, renderTournamentColumn, renderCompetitorsColumn,
    renderMatchIdColumn, renderStartDatetimeColumn, renderStatusColumn, renderReportColumns,
} from '@modules/propertyLicence/utils/propertyLicenceTableHelper';

const undefinedCompetitorsText = COMPETITOR_NOT_DEFINED_BOTH;

export const name = (home, away) => (
    (home || away) ?
        `${home ? home.name : COMPETITOR_NOT_DEFINED} : ${away ? away.name : COMPETITOR_NOT_DEFINED}` :
        undefinedCompetitorsText
);

export function createColumns (propertyLicenceEvents, propertyLicenceTypeId) {
    const columns = {
        'id': {
            label: 'ID',
            sorting: (row) => convertToInt(_get(row, 'id.value', 0)),
            header: {
                align: 'center',
                isCollapsed: true,
            },
            content: {
                align: 'center',
            },
        },
        'match_id': {
            label: 'Match ID',
            sorting: (row) => convertToInt(_get(row, 'match_id.value', 0)),
            isHidden: propertyLicenceTypeId !== PROPERTY_LICENCE_TYPE_SPORTRADAR,
            header: {
                align: 'center',
                isCollapsed: true,
            },
            content: {
                align: 'center',
            },
        },
        'start_datetime': {
            label: 'Date/time',
            sorting: (row) => moment(_get(row, 'start_datetime.value', 0)).valueOf(),
            header: {
                align: 'center',
            },
            content: {
                align: 'center',
                singleLine: true,
            },
        },
        'category': {
            label: 'Category',
            sorting: row => _get(row, 'category.value', ''),
            isHidden: propertyLicenceTypeId !== PROPERTY_LICENCE_TYPE_SPORTRADAR,
        },
        'content_category_level2': {
            label: '2nd level category',
            sorting: row => _get(row, 'content_category_level2.value', ''),
            isHidden: propertyLicenceTypeId !== PROPERTY_LICENCE_TYPE_OTHER,
        },
        'tournament': {
            label: 'Tournament',
            sorting: row => _get(row, 'tournament.value', ''),
            isHidden: propertyLicenceTypeId !== PROPERTY_LICENCE_TYPE_SPORTRADAR,
        },
        'content_category_level3': {
            label: '3rd level category',
            sorting: row => _get(row, 'content_category_level3.value', ''),
            isHidden: propertyLicenceTypeId !== PROPERTY_LICENCE_TYPE_OTHER,
        },
        'competitors': {
            label: 'Competitors / Description',
            sorting: (row) => _get(row, 'competitors.value', ''),
        },
        'event_status': {
            label: 'Status',
            sorting: (row) => _get(row, 'event_status.name', ''),
        },
        'products': {
            label: 'Products',
            content: {
                singleLine: true,
            },
        },
        'bookings': {
            label: 'Bookings',
            sorting: (row) => convertToInt(_get(row, 'bookings.value', 0)),
            header: {
                align: 'center',
            },
            content: {
                align: 'center',
            },
        },
        'report_type': {
            label: 'Report types',
            sorting: (row) => _get(row, 'reports[0].report_type.name', ' '),
        },
        'incident_source': {
            label: 'Incident sources',
            sorting: (row) => _get(row, 'reports[0].incident_source.name', ' '),
        },
        'incident_type': {
            label: 'Incident types',
            sorting: (row) => _get(row, 'reports[0].incident_type.name', ' '),
        },
        'comment': {
            label: 'Comments',
            sorting: (row) => _get(row, 'reports[0].comment', ' '),
        },
    };

    if (0 < propertyLicenceEvents.length) {
        propertyLicenceEvents[0].products_invoice_status.forEach((productInvoiceStatus) => {
            const columnKey = `invoice_status_${getProductShortNameWithOnlyLettersAndDigits(productInvoiceStatus.short_name)}`;
            Object.assign(columns, {
                [columnKey]: {
                    isHidden: !productInvoiceStatus.has_usage,
                    label: `Invoice status ${productInvoiceStatus.short_name}`,
                    sorting: (row) => _get(row, `${columnKey}.value`, ' '),
                },
            });
        });
    }

    return columns;
}

export const PropertyLicenceEventsTable = (props) => {
    const {loading, propertyLicenceEvents, propertyLicenceTypeId} = props;
    const columns = useMemo(() => createColumns(
        propertyLicenceEvents,
        propertyLicenceTypeId
    ), [propertyLicenceEvents, propertyLicenceTypeId]);

    const rowRenderer = (column, row) => {
        switch (column) {
            case 'id':
                return renderIdColumn(row);
            case 'match_id':
                return renderMatchIdColumn(column, row);
            case 'start_datetime':
                return renderStartDatetimeColumn(column, row);
            case 'competitors':
                return renderCompetitorsColumn(row);
            case 'event_status':
                return renderStatusColumn(column, row);
            case 'report_type':
            case 'incident_type':
            case 'incident_source':
            case 'comment':
                return renderReportColumns(column, row);
            case 'category':
            case 'content_category_level2':
            case 'tournament':
            case 'content_category_level3':
            case 'products':
            case 'bookings':
                return row[column]?.value;
            default:
                if (column.includes('invoice_status')) {
                    return row[column]?.value;
                }

                return null;
        }
    };

    const propertyLicenceEventsForTable = propertyLicenceEvents.map(event => {
        event = addHasMatchIdToStructure(event);
        let invoice_status_columns = {};

        event.products_invoice_status.forEach((productInvoiceStatus) => {
            Object.assign(invoice_status_columns, {
                [`invoice_status_${getProductShortNameWithOnlyLettersAndDigits(productInvoiceStatus.short_name)}`]: _get(
                    productInvoiceStatus,
                    'invoice_status.name',
                    ''
                ),
            });
        });

        const reports = event.reports?.length ? event.reports : [null];
        const rowSpan = reports.length;

        const buildReportColumns = (report) => ({
            report_type: {value: _get(report, 'report_type.name', '')},
            incident_source: {value: _get(report, 'incident_source.name', '')},
            incident_type: {value: _get(report, 'incident_type.name', '')},
            comment: {value: _get(report, 'comment', '')},
        });

        const columns = {
            id: {value: event.id, rowSpan},
            match_id: {value: event.sr_event?.id, rowSpan},
            start_datetime: {value: event.start_datetime, rowSpan},
            category: {value: renderCategoryColumn(event), rowSpan},
            content_category_level2: {value: renderContentCategoryColumn(event, 2), rowSpan},
            tournament: {value: renderTournamentColumn(event), rowSpan},
            content_category_level3: {value: renderContentCategoryColumn(event, 3), rowSpan},
            competitors: {value: getCompetitorsNameFromEvent(event), rowSpan},
            event_status: (event.event_status ? {
                id: event.event_status.id,
                name: event.event_status.name,
                rowSpan,
            } : null),
            products: {value: renderProductsColumn('products', event), rowSpan},
            bookings: {value: event.bookings, rowSpan},
            ...Object.keys(invoice_status_columns).reduce((acc, key) => ({
                ...acc,
                [key]: {value: invoice_status_columns[key], rowSpan},
            }), {}),
        };

        const [firstReport, ...restReports] = reports;

        return {
            ...event,
            ...buildReportColumns(firstReport),
            ...columns,
            currentReport: firstReport,
            subRows: restReports?.map((report) => ({
                ...buildReportColumns(report),
                currentReport: report,
            })),
        };
    });

    return (
        <div className='propertyLicenceEvents__table'>
            <Table
                className='mini'
                columns={columns}
                data={propertyLicenceEventsForTable}
                showLimitWarning={true}
                dataLimitWarning={10000}
                loading={loading}
                name='propertyLicenceEventsList'
                noDataText='No events found'
                rowRenderer={rowRenderer}
            />
        </div>
    );
};

PropertyLicenceEventsTable.defaultProps = {
    propertyLicenceEvents: [],
};

PropertyLicenceEventsTable.propTypes = {
    loading: PropTypes.bool.isRequired,
    propertyLicenceEvents: PropTypes.array,
    propertyLicenceTypeId: PropTypes.number.isRequired,
};

export default PropertyLicenceEventsTable;
